/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

/**
 * Represents the main class of the NVA framework.
 */

export default class $ {

    /**
     * Checks that the specified object is a number or not.
     *
     * @param   {Object}    object     object that has to check
     * @return  {Boolean}   isnumber   object is number or not
     */
    
    static isNumber(object) {
        return typeof(object) === 'number';
    }
    
    /**
     * Checks that the specified object is a string or not.
     *
     * @param   {Object}    object     object that has to check
     * @return  {Boolean}   isstring   object is string or not
     */
    
    static isString(object) {
        return typeof(object) === 'string';
    }

    /**
     * Checks that the specified object is a function or not.
     *
     * @param   {Object}    object      object that has to check
     * @return  {Boolean}   isfunction  object is function or not
     */
    
    static isFunction(object) {
        return {}.toString.call(object) === '[object Function]';
    }

    /**
     * Checks that the specified object is an array or not.
     *
     * @param   {Object}    object     object that has to check
     * @return  {Boolean}   isarray    object is array or not
     */
    
    static isArray(object) {
        return Object.prototype.toString.call(object) === '[object Array]';
    }

    /**
     * Checks that the specified object is a JSON object or not.
     *
     * @param   {Object}    object      object that has to check
     * @return  {Boolean}   isobject    object is JSON object or not
     */
    
    static isObject(object) {
        return object !== undefined && object !== null &&
               (typeof(object) === 'object');
    }
    
    /**
     * Checks that the specified object is a Date object or not.
     *
     * @param   {Object}    object      object that has to check
     * @return  {Boolean}   isdate      object is a date or not
     */

    static isDate(date) {
        return (date !== undefined && date !== null && 
               date instanceof Date && !isNaN(date.valueOf()));
    }

    /**
     * Checks that the specified object has a primitive type or not.
     *
     * @param   {Object}    object        object that has to check
     * @return  {Boolean}   isprimitive   object has primitive type or not
     */
    
    static isPrimitive(object) {
        let type = typeof(object);
        return object !== undefined && object !== null &&
               (type !== 'object' && type !== 'function');
    }

    /**
     * Checks that the specified object is empty (undefined, null) or not.
     * 
     * @param     {Object}     object    object that has to check
     * @return    {Boolean}    isempty   object is empty or not
     */
     
    static isEmpty(value) {
        return value === undefined || value === null;
    }
    
    /**
     * Does the string ends with the specified suffix?
     *
     * @param   {String}  text      text that has to check
     * @param   {String}  suffix    suffix that has to check
     * @return  {Boolean} endswith  does the string ends with the suffix
     */

    static endsWith(text, suffix) {
        return (text && suffix) ? 
               text.indexOf(suffix, text.length - suffix.length) !== -1 : 
               false;
    }

    /**
     * Replaces all of the occurances of the specified string with another.
     *
     * @param   {String}  text      string where the replace will be
     * @param   {String}  find      string that has to find
     * @param   {String}  replace   string that is the substitute string
     * @return  {String}  result    new replaced string
     */

    static replaceAll(text, find, replace) {
        return (text && find) ? text.replace(
            new RegExp(find, 'g'), 
            (replace === '' || replace) ? replace : find
        ) : text;
    }

    /**
     * Checks that does the string contain digit (0-9) characters only or not.
     *
     * @param   {String}  text      string that has to check
     * @return  {Boolean} digit     string contains digit only or not
     */

    static isDigit(text) {

        // Checking string

        if (this.isEmpty(text) || !this.isString(text) || text.length === 0) {
            return false;
        }

        for (let i = 0; i < text.length; i++) {
            if (isNaN(parseInt(text[i], 10))) {
                return false;
            }
        }

        // String contains digit only

        return true;
    }

    /**
     * Checks that does the string contain alpha (a-z) characters only or not.
     *
     * @param   {String}  text      string that has to check
     * @return  {Boolean} alpha     string contains alpha characters only or not
     */

    static isAlpha(text) {

        // Checking string

        if (this.isEmpty(text) || !this.isString(text) || text.length === 0) {
            return false;
        }

        return text.search(/[^A-Za-z]/) === -1;
    }

    /**
     * Capitalizes the first letter of the string.
     *
     * @param   {String}  text      string that has to capitalize
     * @return  {String}  result    capitalized string
     */
    
    static capitalizeFirstLetter(text) {
        
        // Checking string
        
        if (this.isEmpty(text) || !this.isString(text) || text.length === 0) {
            return this;
        }
        
        // Capitalizing the first letter

        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    /**
     * Checks that the specified value is empty (undefined, null) and if yes,
     * it returns the substitute value. If no, then the value will be
     * returned.
     * 
     * @param     {Object}  value        value that has to check
     * @param     {Object}  substitute   substitute value that has to return
     *                                   if the value is empty
     * @return    {Object}  result       result of the comparison
     *                                   (value or substitute value)
     */
    
    static NVL(value, substitute) {
        return this.isEmpty(value) ? substitute : value;
    }

    /**
     * Callbacks the specified function.
     * 
     * @param   {Function}  callback     callback function that has to invoke
     */
 
    static callback(callback) {
        if (this.isFunction(callback)) {
            callback.apply(this, Array.prototype.slice.call(arguments, 1));   
        }
    }

    /**
     * Merges the specified JSON objects and returns result. The target object
     * will be changed.
     *
     * @param  {Object}     target      target object
     * @param  {Object}     source      source object
     * @param  {Object}     result      result after the merging
     */
    
    static merge(target, source) {
    
        // Checking the type of the target object
       
        if (typeof(target) !== 'object') {
            target = {};
        }
        
        // Getting properties of the source object and adding these to the
        // target.
    
        for (let property in source) {
            if (source.hasOwnProperty(property)) {
                let sourceProperty = source[property];
                
                // Calling recursively in case of JSON object in the property
                // value.
    
                if (typeof(sourceProperty) === 'object') {
                    target[property] = this.merge(
                        target[property],
                        sourceProperty
                    );
                    continue;
                }
                
                target[property] = sourceProperty;
            }
        }
        
        // Returns the target object
    
        return target;
    }
   
    /**
     * Loads the specified scripts by URL synchroniusly and callbacks after the
     * loading.
     * 
     * @param     {String/Array}    scripts      script URLs that has to load
     * @return    {Promise}         promise      promise object
     */

    static loadScript(scripts) {
        
        // Creating promise for the script loading

        return new Promise(function(resolve, reject) {

            // Define the script loader

            let loader = (script, callback) => {

                // Checking script
                
                if (!$.isArray(script)) {
                    script = [script];
                }

                // Creating script element
                
                let scriptElement = document.createElement('script');
                
                // Load event

                scriptElement.onload = function() {
                    
                    // Removing the script tag from header after the load

                    document.getElementsByTagName('head')[0].removeChild(
                        scriptElement
                    );

                    // Is there any script that has to load?
                    
                    if (script.length > 1) {
                        script.splice(0, 1);
                        loader(script, callback);
                    } else {
                        
                        // Callbacking after the loading
                        
                        if (callback) {
                            callback();
                        }
                    }
                };
                
                // Adding script to the header and loading it
                
                scriptElement.src = script[0];
                document.getElementsByTagName('head')[0].appendChild(
                    scriptElement
                );
            };

            // Loading scripts

            loader(scripts, function() {

                // Loading finished

                resolve();
            });
        });
    }

    /**
     * Gets the value of the specified object and attribute.
     *
     * @param   {Object}  object      object that has to observe
     * @param   {String}  attribute   attribute of the object
     * @return  {Object}  object      value of the attribute
     */
    
    static getAttr(object, attribute) {
    
        // Processing attribute
    
        if (object && attribute && typeof(object) === 'object' &&
        typeof(attribute) === 'string') {
            attribute = attribute.split('.');
    
            // Getting the attribute
    
            if (attribute[0]) {
                while (object && attribute[0]) {
                    object = object[attribute.shift()];
                }
            } else {
                return undefined;
            }
    
            // Returns the attribute value
    
            return object;
        } else {
    
            // Empty or invalid parameters
    
            return undefined;
        }
    }

    /**
     * Converts the specified object to array and returns it. An empty array will be
     * returned if the object is empty (undefined or null). If there are more
     * attributes specified, the conversion will be called recursively for all
     * of the specified attributes.
     * <p>
     * @param   {Object}        object      object that has to convert
     * @param   {String}        attribute   attribute names
     * @return  {Array}         array       converted object as an array
     */

    static getArray(object, attribute) {

        // Checking the parameters
    
        if (arguments.length === 0) {
            return [];
        } else if (arguments.length === 1) {
    
            // Converting the object to array
    
            if (!object) {
                return [];
            } else if (this.isArray(object)) {
                return object;
            } else {
                return [object];
            }
        } else {
    
            // Checking and searching the attributes
    
            let result = attribute ? this.getAttr(object, attribute) : object;
            return this.isEmpty(result) ? [] : 
                   (this.isArray(result) ? result : [result]);
        }
    }

    /**
     * Searches the specified value in the specified attribute of the array and
     * returns the item at the first match. If the value does not found or the
     * array is not a valid array, an undefined value will be returned. 
     *
     * @param   {Array}     array     array that is the subject of the searching
     * @param   {String}    attr      attribute where the searching will be done
     * @param   {Any}       value     value that will be searched
     * @return  {Any}       item      item of the array if the searching was
     *                                success or undefined if not
     */
    
    static searchInArray(array, attr, value) {
    
        // Is it an array?
    
        if (this.isArray(array)) {
    
            // Searching the value
    
            for (let i = 0; i < array.length; i++) {
                if (this.getAttr(array[i], attr) === value) {
    
                    // Returns the item
    
                    return array[i];
                }
            }
        }
        
        // There is no match or it is not an array
    
        return undefined;
    }

    /**
     * Escapes the specified HTML string and returns it. It replaces the &, <, "
     * characters with &amp;, &lt;, &quot; symbols.
     * 
     * @param     {String}     html     HTML string that has to observe
     * @return    {String}     result   corrected HTML string
     */
    
    static escapeHTML(html) {
        return (!this.isEmpty(html) && this.isString(html)) ?
               html.split('&').join('&amp;').split('<').join('&lt;')
               .split('"').join('&quot;') : html;
    }

    /**
     * Validates the specified email address and returns that is it valid or not.
     * 
     * @param   {String}    email       email address that has to check
     * @return  {Boolean}   valid       email address is valid or not
     */
    
    static isEmailValid(email) {
        let re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(email);
    }

    /**
     * Escapes the specified string by replacing NEWLINE characters (CR/LF) with
     * <BR> text and returns it.
     * 
     * @param     {String}     value   string that has to observe
     * @return    {String}     result  corrected string
     */
    
    static escapeNL(value) {
        
        // Checking and searching the CR LF characters
    
        if (value) {
            let CR = String.fromCharCode(13);
            let LF = String.fromCharCode(10);
            let NL = '###br###';
    
            value = value.replaceAll(CR + LF, NL);
            value = value.replaceAll(LF + CR, NL);
            value = value.replaceAll(CR, NL);
            value = value.replaceAll(LF, NL);
        }
    
        // Returns the escaped value
    
        return value;
    }

    /**
     * Formats a number.
     * 
     * @param   {Number}    number      number that has to format
     * @param   {Number}    n           length of decimal
     * @param   {Number}    x           length of whole part
     * @param   {String}    s           sections delimiter
     * @param   {String}    c           decimal delimiter
     * @return  {String}    formatted   formatted number
     */
    
    static formatNumber(number, n, x, s, c) {
    
        // Preparing
    
        let re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\D' : '$') + ')';
        let num = number.toFixed(Math.max(0, ~~n));
    
        // Formatting number
    
        return (c ? num.replace('.', c) : num).replace(
            new RegExp(re, 'g'),
            '$&' + (s || ',')
        );
    }

    /**
     * Case-insensitive sorter function.
     *
     * @param   {String}    attr          name of the attribute
     * @param   {String}    direction     direction of the sorting (asc/desc)
     * @return  {Function}  sorter        generated sorter function for the direction
     */
    
    static sortData(attr, direction) {
    
        // Defining case-insensitive sorter function
    
        return function(a, b) {
            
            // Preparing
            
            var valueA;
            var valueB;
    
            if (attr) {
                valueA = a[attr];
                valueB = b[attr];
            } else {
                valueA = a;
                valueB = b;
            }
    
            if ($.isString(valueA)) {
                valueA = valueA.toLowerCase();
            } else {
                valueA = valueA || '';
            }
    
            if ($.isString(valueB)) {
                valueB = valueB.toLowerCase();
            } else {
                valueB = valueB || '';
            }
            
            // Comparing
            
            if ((direction || 'asc') === 'asc') {
                return ((valueA < valueB) ? -1 : ((valueA > valueB) ? 1 : 0));
            } else {
                return ((valueA > valueB) ? -1 : ((valueA < valueB) ? 1 : 0));
            }
        };
    }

    /**
     * Gets the parameters of the specified URL or if it does not specified then the
     * current one. It returns the parameters as a JSON object. If there is no
     * parameter in the URL, an empty object will be returned.
     * 
     * @param     {String}     url        URL that has to observe
     * @return    {Object}     params     URL parameters
     */

    static getURLParameters(url) {
    
        // Preparing

        var urlString = ((url && $.isString(url)) ? url : undefined) || 
                        window.location.search;

        if (urlString && urlString.indexOf('?') === 0) {
            urlString = urlString.substring(1);
        }

        var pairs = urlString.split('#')[0].split("&");
        var obj = {};
        var pair;

        // Parsing the parameters

        for (var i in pairs) {
            if (pairs[i] === '') {
                continue;
            }

            pair = pairs[i].split('=');
            obj[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
        }

        // Returns the JSON object

        return obj;
    }

    /**
     * Fires the specified event with the specified event data.
     *
     * @param {String}    eventname  name of the event
     * @param {Object}    data       event data
     */

    static fireEvent(eventname, data) {
        document.body.dispatchEvent(new CustomEvent(eventname, { detail: data }));
    }

    /**
     * Listen to the specified event and calls the specified event handler in case
     * of event firing.
     *
     * @param {String}    eventname  name of the event
     * @param {Function}  handler    event handler function
     */

    static listenEvent(eventname, handler) {
        document.body.addEventListener(eventname, (event) => {
            handler(event.detail);
        }, false);
    }    
}



// WEBPACK FOOTER //
// ./src/components/Util/Util.js